// Pure-SVG charts — no chart-lib dependency, kept under ~200 lines.
// Designed for the dashboard's quiet, density-honest aesthetic.

import type { ChannelEntry, OccupancyDay, RevenueLine } from './api/types'

const HEAT_RAMP = [
  'hsl(var(--heat-0))',
  'hsl(var(--heat-1))',
  'hsl(var(--heat-2))',
  'hsl(var(--heat-3))',
  'hsl(var(--heat-4))',
]

function heatBucket(rate: number): string {
  if (rate <= 0.01) return HEAT_RAMP[0]
  if (rate < 0.3) return HEAT_RAMP[1]
  if (rate < 0.6) return HEAT_RAMP[2]
  if (rate < 0.85) return HEAT_RAMP[3]
  return HEAT_RAMP[4]
}

const PALETTE: Record<string, string> = {
  line: '#2f7a55',
  own_site: '#2563a8',
  google: '#b35d00',
  cherie: '#a8336d',
  staff: '#5b5e57',
  phone: '#1b1d1a',
  unknown: '#8d8f88',
}

export function ChannelLabel(props: { source: string }): string {
  // (i18n key suffix — caller wraps in t())
  return props.source
}

/** Heat strip — one cell per day, colored by rate. */
export function OccupancyHeatStrip({ days }: { days: OccupancyDay[] }) {
  if (!days.length) return null
  const cols = days.length
  // Show ~6 axis labels max so they don't crowd. Pick stride accordingly.
  const labelEvery = Math.max(1, Math.ceil(cols / 6))
  return (
    <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {days.map((d, i) => {
        const dt = new Date(d.date)
        const showLabel = i === 0 || i === cols - 1 || i % labelEvery === 0
        const label = `${dt.getMonth() + 1}/${dt.getDate()}`
        return (
          <div
            key={d.date}
            className="heat-cell"
            style={{ background: heatBucket(d.rate) }}
            title={`${d.date} · ${Math.round(d.rate * 100)}% (${d.occupied}/${d.capacity})`}
          >
            {showLabel && <span>{label}</span>}
          </div>
        )
      })}
    </div>
  )
}

/** Compact donut for channel attribution. */
export function ChannelDonut({
  channels,
  total,
  size = 156,
}: {
  channels: ChannelEntry[]
  total: number
  size?: number
}) {
  const radius = size / 2
  const inner = radius * 0.62
  const cx = radius
  const cy = radius
  if (!total) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={radius - 2} fill="hsl(var(--secondary))" />
      </svg>
    )
  }
  let acc = 0
  const slices = channels.map((c) => {
    const start = (acc / total) * Math.PI * 2
    acc += c.count
    const end = (acc / total) * Math.PI * 2
    const large = end - start > Math.PI ? 1 : 0
    const x1 = cx + radius * Math.sin(start)
    const y1 = cy - radius * Math.cos(start)
    const x2 = cx + radius * Math.sin(end)
    const y2 = cy - radius * Math.cos(end)
    const ix1 = cx + inner * Math.sin(start)
    const iy1 = cy - inner * Math.cos(start)
    const ix2 = cx + inner * Math.sin(end)
    const iy2 = cy - inner * Math.cos(end)
    const d = [
      `M ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2}`,
      `L ${ix2} ${iy2}`,
      `A ${inner} ${inner} 0 ${large} 0 ${ix1} ${iy1}`,
      'Z',
    ].join(' ')
    return { d, fill: PALETTE[c.source] ?? PALETTE.unknown, source: c.source }
  })
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map((s, i) => (
        <path key={i} d={s.d} fill={s.fill} />
      ))}
      <circle cx={cx} cy={cy} r={inner - 1} fill="hsl(var(--card))" />
      <text
        x={cx}
        y={cy - 2}
        textAnchor="middle"
        fontSize="11"
        fill="hsl(var(--muted-foreground))"
      >
        bookings
      </text>
      <text
        x={cx}
        y={cy + 14}
        textAnchor="middle"
        fontSize="20"
        fontWeight={700}
        fill="hsl(var(--foreground))"
      >
        {total}
      </text>
    </svg>
  )
}

/** Stacked horizontal bars — boarding/daycare/grooming revenue at a glance. */
export function ServiceMixBars({
  rows,
  fmt,
}: {
  rows: Array<{ label: string; paid: number; gross: number; color: string }>
  fmt: (n: number) => string
}) {
  const max = Math.max(1, ...rows.map((r) => r.gross))
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {rows.map((r) => {
        const grossPct = (r.gross / max) * 100
        const paidPct = (r.paid / max) * 100
        return (
          <div key={r.label}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: 'hsl(var(--muted-foreground))',
                marginBottom: 4,
              }}
            >
              <span>{r.label}</span>
              <span>
                <strong style={{ color: 'hsl(var(--foreground))' }}>{fmt(r.paid)}</strong>{' '}
                <span style={{ color: 'hsl(var(--muted-foreground) / 0.7)' }}>/ {fmt(r.gross)}</span>
              </span>
            </div>
            <div
              style={{
                position: 'relative',
                background: 'hsl(var(--secondary))',
                height: 12,
                borderRadius: 6,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: `${grossPct}%`,
                  background: r.color,
                  opacity: 0.25,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: `${paidPct}%`,
                  background: r.color,
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function revenueRows(
  rev: { boarding: RevenueLine; daycare: RevenueLine; grooming: RevenueLine },
  labels: { boarding: string; daycare: string; grooming: string },
): Array<{ label: string; paid: number; gross: number; color: string }> {
  return [
    { label: labels.boarding, paid: rev.boarding.paid, gross: rev.boarding.gross, color: '#2f7a55' },
    { label: labels.daycare, paid: rev.daycare.paid, gross: rev.daycare.gross, color: '#2563a8' },
    { label: labels.grooming, paid: rev.grooming.paid, gross: rev.grooming.gross, color: '#a8336d' },
  ]
}
